/**
 * Apprise-on-Azure-VM wizard defaults (pure logic) — contract v2, "Azure Create-VM
 * experience". The defaults come from the Azure portal screenshots (see appriseCatalog):
 * pinned subscription, shared resource group, Windows Server 2025, Standard_D2s_v3,
 * new isolated vnet / subnet / public IP, Basic NSG, no inbound ports, no data disks.
 *
 * Nothing about Apprise's runtime is verified, so no inbound port, no data disk and
 * no application installer is ever defaulted.
 */
import type {
  AppriseImageRef, AppriseNetwork, AppriseOperations, AppriseOsType, AppriseSecurity, AppriseServer,
  AppriseServerEnvironment, AppriseStorage, AppriseVmConfig, AppriseVmImage, AppriseVmOptions, AzureAvailabilityOption,
  AzureImage, AzureSecurityTypeOption, Environment, SizeResources, VmSizing,
} from '@/types'
import { PRODUCTION_ENVS, regionToLocation } from './azureNames'
import { APPRISE_UNVERIFIED_NOTICE } from '@/utils/apprise'
import {
  DEFAULT_ADMIN_USERNAME, DEFAULT_IMAGE, DEFAULT_RESOURCE_GROUP, DEFAULT_SUBNET_PREFIX, DEFAULT_VM_NAME, DEFAULT_VM_SIZE,
  DEFAULT_VNET_ADDRESS_SPACE,
} from './appriseCatalog'
import { defaultCredentialMode } from './appriseCredentials'

/** Seeded image catalog (contract v2 §2) — used only for the "verified for Apprise" annotation and the admin list. */
export const FALLBACK_APPRISE_IMAGES: AppriseVmImage[] = [
  { id: 5, os_type: 'windows', label: 'Windows Server 2025 Datacenter - x64 Gen2', publisher: 'MicrosoftWindowsServer', offer: 'WindowsServer', sku: '2025-datacenter-g2', version: 'latest', verified_for_product: false },
  { id: 1, os_type: 'windows', label: 'Windows Server 2022 Datacenter: Azure Edition', publisher: 'MicrosoftWindowsServer', offer: 'WindowsServer', sku: '2022-datacenter-azure-edition', version: 'latest', verified_for_product: false },
  { id: 2, os_type: 'windows', label: 'Windows Server 2019 Datacenter Gen2', publisher: 'MicrosoftWindowsServer', offer: 'WindowsServer', sku: '2019-datacenter-gensecond', version: 'latest', verified_for_product: false },
]

/** Contract defaults — used until / unless the options endpoint answers. */
export const FALLBACK_APPRISE_OPTIONS: AppriseVmOptions = {
  images: FALLBACK_APPRISE_IMAGES,
  os_types: ['windows'],
  windows_server_versions: ['2019', '2022', '2025'],
  disk_types: ['Premium_LRS', 'StandardSSD_LRS', 'Standard_LRS', 'Premium_ZRS', 'StandardSSD_ZRS'],
  disk_caching: ['None', 'ReadOnly', 'ReadWrite'],
  identity_modes: ['system_assigned', 'user_assigned', 'none'],
  admin_access_methods: ['managed_outside', 'azure_bastion', 'private_network', 'jit'],
  outbound_modes: ['internet_allowed', 'nat_gateway', 'restricted'],
  server_environments: ['development', 'staging', 'production'],
  availability_zone_regions: ['eastus', 'eastus2', 'centralus', 'southcentralus', 'westus2', 'westus3', 'canadacentral', 'northeurope', 'westeurope', 'uksouth'],
  allowed_regions: ['australiasoutheast', 'canadacentral', 'centralus', 'eastus', 'eastus2', 'northcentralus', 'southcentralus', 'uksouth', 'westus', 'westus2'],
  allowed_environments: ['dev'],
  allow_new_virtual_network: false,
  backup_frequencies: ['daily', 'weekly'],
  notice: APPRISE_UNVERIFIED_NOTICE,
  default_password_available: false,
  subscription_pinned: true,
}

/** Catalog fallback mirroring the seeded Apprise Small size (contract v2 §2). */
export const FALLBACK_VM_SIZING: VmSizing = {
  vm_size: DEFAULT_VM_SIZE,
  vcpu: 2,
  memory_gb: 8,
  os_disk_gb: null, // image default
  os_disk_type: 'StandardSSD_LRS',
  public_ip_default: true,
  identity_default: 'none',
  monitoring_default: false,
  backup_default: false,
  overrides_allowed: true,
}

export function appriseRegionSupportsZones(region: string, options: Pick<AppriseVmOptions, 'availability_zone_regions'>): boolean {
  return options.availability_zone_regions.map((r) => r.toLowerCase()).includes(regionToLocation(region))
}

/** Portal environment → Azure server environment (not shown; derived). */
export function serverEnvironmentFor(env: Environment | string): AppriseServerEnvironment {
  if (PRODUCTION_ENVS.has(env)) return 'production'
  if (env === 'dev') return 'development'
  return 'staging'
}

/** What the wizard already knows from the live catalog — used to keep the form coherent. */
export interface AppriseCatalogSnapshot {
  /** Names of the existing resource groups (undefined while loading). */
  resourceGroups?: string[]
  /** Existing virtual networks of the region (undefined while loading): new defaults skip their names. */
  virtualNetworks?: Array<{ name: string; resource_group: string }>
  availabilityOptions?: AzureAvailabilityOption[]
  securityTypes?: AzureSecurityTypeOption[]
}

export interface AppriseDefaultsContext {
  environment: Environment | string
  /** Region as stored by the wizard: display name ("East US 2") or location code. */
  region: string
  sku: string
  /** Sizing of the selected Apprise size config (undefined while loading). */
  resources?: SizeResources
  options: AppriseVmOptions
  catalog?: AppriseCatalogSnapshot
}

/** Catalog VM sizing for the context (falls back to the seeded Small values). */
export function catalogVmSizing(ctx: Pick<AppriseDefaultsContext, 'resources'>): VmSizing {
  return ctx.resources?.vm ?? FALLBACK_VM_SIZING
}

/** Production defaults backup on; otherwise the catalog value. */
export function backupDefaultFor(serverEnvironment: AppriseServerEnvironment, catalog: VmSizing): boolean {
  return serverEnvironment === 'production' ? true : catalog.backup_default
}

const trimHyphens = (s: string) => s.replace(/-+$/g, '')

/** Windows computer name: the VM name truncated to 15 characters; Linux: lower-case, up to 63. */
export function deriveHostname(serverName: string, os: AppriseOsType | ''): string {
  const name = serverName.trim()
  return os === 'linux' ? name.toLowerCase().slice(0, 63) : name.slice(0, 15)
}

/** Whether the form may offer a NEW virtual network (the server's option; missing = allowed). */
export function newVirtualNetworkAllowed(options: Pick<AppriseVmOptions, 'allow_new_virtual_network'>): boolean {
  return options.allow_new_virtual_network !== false
}

/** Names of the existing virtual networks that sit in the given resource group (a new one must not reuse them). */
export function takenVnetNames(
  vnets: Array<{ name: string; resource_group: string }> | undefined, resourceGroup: string,
): string[] {
  const rg = resourceGroup.trim().toLowerCase()
  return (vnets ?? []).filter((v) => v.resource_group.trim().toLowerCase() === rg).map((v) => v.name)
}

/** The first `<prefix>-<n>` (n from 1) that none of the taken names uses (case-insensitive). */
function firstFreeIndex(prefix: string, taken: string[]): number {
  const used = new Set(taken.map((t) => t.toLowerCase()))
  let n = 1
  while (used.has(`${prefix}-${n}`.toLowerCase())) n += 1
  return n
}

/**
 * Names that embed the VM name or the region (re-derived while the user has not edited them). A new virtual network
 * never gets the name of one that already exists in the resource group: the number counts up (vnet-eastus2-1, -2, ...)
 * and the subnet follows it.
 */
export function derivedNames(serverName: string, region: string, takenVnets: string[] = []) {
  const loc = regionToLocation(region) || 'eastus2'
  const vm = serverName.trim() || DEFAULT_VM_NAME
  const n = firstFreeIndex(`vnet-${loc}`, takenVnets)
  return {
    vnet_name: `vnet-${loc}-${n}`,
    subnet_name: `snet-${loc}-${n}`,
    public_ip_name: `${vm}-ip`,
    nsg_name: `${vm}-nsg`,
    key_vault_name: trimHyphens(`kv-${vm}`.slice(0, 24)),
    backup_vault_name: trimHyphens(`rsv-${vm}`.slice(0, 50)),
  }
}

export function defaultBackupPolicy(vaultName: string) {
  return { frequency: 'daily' as const, time: '23:00', retention_days: 30, vault_mode: 'create_new' as const, vault_name: vaultName, vault_resource_group: '' }
}

/** Whether the named resource group exists in the (live) list; the default group is known to exist. */
export function resourceGroupExists(name: string, existing: string[] | undefined): boolean {
  const n = name.trim().toLowerCase()
  if (!n) return false
  if (existing) return existing.some((g) => g.toLowerCase() === n)
  return n === DEFAULT_RESOURCE_GROUP.toLowerCase()
}

export function imageRefOf(image: Pick<AzureImage | AppriseVmImage, 'publisher' | 'offer' | 'sku' | 'version'> | undefined): AppriseImageRef {
  return image
    ? { publisher: image.publisher, offer: image.offer, sku: image.sku, version: image.version || 'latest' }
    : { ...DEFAULT_IMAGE }
}

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

export interface ServerDefaults {
  resource_group_name: string
  server_name: string
  vm_size?: string
  admin_username: string
  image: AppriseImageRef
  /** Existing Key Vault the server presets for the admin credential; null when none is configured. */
  key_vault: { name: string; resource_group: string } | null
}

/**
 * The options endpoint's `defaults` object is read leniently (its key names are not part of
 * the contract): known keys of the right type override the built-in §1 defaults, anything
 * else is ignored.
 */
export function readServerDefaults(defaults: Record<string, unknown> | undefined): ServerDefaults {
  const d = defaults ?? {}
  const img = d.image && typeof d.image === 'object' ? (d.image as Record<string, unknown>) : undefined
  const image =
    img && str(img.publisher) && str(img.offer) && str(img.sku)
      ? { publisher: str(img.publisher)!, offer: str(img.offer)!, sku: str(img.sku)!, version: str(img.version) ?? 'latest' }
      : { ...DEFAULT_IMAGE }
  const vault = d.key_vault && typeof d.key_vault === 'object' ? (d.key_vault as Record<string, unknown>) : undefined
  const key_vault = vault && str(vault.name) && str(vault.resource_group) ? { name: str(vault.name)!, resource_group: str(vault.resource_group)! } : null
  return {
    resource_group_name: str(d.resource_group) ?? str(d.resource_group_name) ?? DEFAULT_RESOURCE_GROUP,
    // an empty name from the server is deliberate: the requester types the VM name themself
    server_name: typeof d.vm_name === 'string' ? d.vm_name.trim() : (str(d.server_name) ?? DEFAULT_VM_NAME),
    vm_size: str(d.vm_size) ?? str(d.size),
    admin_username: str(d.admin_username) ?? str(d.username) ?? DEFAULT_ADMIN_USERNAME,
    image,
    key_vault,
  }
}

export function buildDefaultAppriseConfig(ctx: AppriseDefaultsContext): AppriseVmConfig {
  const d = readServerDefaults(ctx.options.defaults)
  const names = derivedNames(d.server_name, ctx.region, takenVnetNames(ctx.catalog?.virtualNetworks, d.resource_group_name))
  const vm = catalogVmSizing(ctx)
  const serverEnvironment = serverEnvironmentFor(ctx.environment)
  const credentialMode = defaultCredentialMode(ctx.options)
  // The operating system starts from the picked size's default image (set by an administrator); without one it is the
  // portal's default, Windows Server 2025. The requester can still choose another image.
  const sizeImage = ctx.resources?.vm?.image
  const os: AppriseOsType = sizeImage ? sizeImage.os_type : 'windows'
  const image = sizeImage
    ? { publisher: sizeImage.publisher, offer: sizeImage.offer, sku: sizeImage.sku, version: sizeImage.version || 'latest' }
    : d.image
  const server: AppriseServer = {
    deployment_mode: 'create_new',
    server_name: d.server_name,
    hostname: deriveHostname(d.server_name, os),
    resource_group_mode: resourceGroupExists(d.resource_group_name, ctx.catalog?.resourceGroups) ? 'existing' : 'create_new',
    resource_group_name: d.resource_group_name,
    os_type: os,
    image,
    // The picked T-shirt size's VM comes first; the backend's generic Azure-form default (Standard_D2s_v3) only
    // applies when the size has no VM profile (or no size is known yet).
    vm_size: ctx.resources?.vm?.vm_size ?? d.vm_size ?? vm.vm_size,
    availability: { type: 'none', zone: '', availability_set_id: '', vmss_id: '' },
    server_environment: serverEnvironment,
    time_zone: '', // not shown; "" = image default
    admin_username: d.admin_username,
    admin_access_method: 'managed_outside', // no admin path is created by the portal; no inbound port is opened
    admin_source_cidrs: [],
    aad_login_enabled: false,
    rbac_admin_principal_ids: [],
    license_type: 'windows_server', // Azure Hybrid Benefit, Windows only
    license_confirmed: true,
  }
  // With new virtual networks switched off the requester must pick an existing one (and a subnet): nothing preselected.
  const existingOnly = !newVirtualNetworkAllowed(ctx.options)
  const network: AppriseNetwork = {
    vnet_mode: existingOnly ? 'existing' : 'create_new',
    vnet_name: existingOnly ? '' : names.vnet_name,
    vnet_address_space: existingOnly ? '' : DEFAULT_VNET_ADDRESS_SPACE,
    vnet_resource_group: '',
    subnet_mode: existingOnly ? 'existing' : 'create_new',
    subnet_name: existingOnly ? '' : names.subnet_name,
    subnet_prefix: existingOnly ? '' : DEFAULT_SUBNET_PREFIX,
    private_ip_allocation: 'dynamic',
    private_ip_address: '',
    public_ip: { mode: vm.public_ip_default ? 'create_new' : 'none', name: names.public_ip_name, resource_group: '' },
    nsg_mode: 'create_new', // "Basic": a new NSG without inbound rules
    nsg_name: names.nsg_name,
    nsg_resource_group: '',
    inbound_rules: [], // PLACEHOLDER: Apprise ports are unknown — "Public inbound ports: None" is fixed
    outbound_mode: 'internet_allowed',
    dns_servers: [],
    dns_label: '',
    load_balancer: { mode: 'none', backend_pool_id: '' },
  }
  const storage: AppriseStorage = {
    os_disk_type: vm.os_disk_type,
    os_disk_gb: vm.os_disk_gb, // null = image default
    data_disk_required: false, // PLACEHOLDER: Apprise storage needs are unknown
    data_disks: [],
    encryption_at_host: false, // the subscription feature is not registered
    disk_encryption_set_id: '',
    backup_required: backupDefaultFor(serverEnvironment, vm),
    backup_policy: defaultBackupPolicy(names.backup_vault_name),
  }
  const security: AppriseSecurity = {
    security_type: 'standard',
    identity_mode: vm.identity_default,
    user_assigned_identity_id: '',
    // A configured vault takes the admin credential in every mode (a password is stored there as a secret); without one,
    // none is valid for password credentials.
    key_vault_mode: d.key_vault ? 'existing' : credentialMode === 'generate_in_key_vault' ? 'create_new' : 'none',
    key_vault_name: d.key_vault?.name ?? names.key_vault_name,
    key_vault_resource_group: d.key_vault?.resource_group ?? '',
    credential_mode: credentialMode,
    credential_secret_name: '',
    // Preferences for "Trusted launch" — the payload builder blanks them for Standard.
    trusted_launch: false,
    secure_boot: true,
    vtpm: true,
    defender_monitoring: true,
    jit_requested: false,
  }
  const operations: AppriseOperations = {
    monitoring_enabled: vm.monitoring_default,
    log_analytics_workspace_id: '',
    boot_diagnostics: { mode: 'managed', storage_account: '', storage_account_resource_group: '' },
    patch_mode: 'manual',
    periodic_assessment: false,
    recommended_alerts: false,
    os_guest_diagnostics: false,
    app_health_monitoring: false,
    tags: {},
  }
  return { sku: ctx.sku, server, network, storage, security, operations }
}

/**
 * Backwards-compatible generic pipeline params, kept consistent with the Apprise
 * block (the generic Load Balancer / Data Disks controls are hidden for Apprise).
 */
export function legacyPipelineParams(config: AppriseVmConfig): {
  create_public_ip: boolean
  load_balancer_required: boolean
  data_disk_sizes: number[]
} {
  const sizes = config.storage.data_disk_required ? config.storage.data_disks.map((d) => d.size_gb) : []
  return {
    create_public_ip: config.network.public_ip.mode !== 'none',
    load_balancer_required: config.network.load_balancer.mode === 'existing',
    data_disk_sizes: sizes,
  }
}
